interface CategorisedDestinations {
  international: Array<App.AirportLocation>
  domestic: Array<App.AirportLocation>
}

/**
 * Strips the "ALL" suffix from meta airport codes (e.g. "MELALL" → "MEL").
 * These codes represent "all airports in a city" and are not accepted by
 * some APIs like calendar-search.
 */
export function normalizeAirportCode(code: string): string {
  const upper = code.toUpperCase()
  return upper.endsWith('ALL') ? upper.slice(0, -3) : upper
}

/**
 * Determines if a destination is effectively the same city as the origin.
 * Uses AirportLocation.cityCode for accurate city-level comparison.
 * Also handles "ALL airports" meta-codes (e.g. MELALL).
 */
export function isSameCityRoute(originCode: string, dest: App.AirportLocation): boolean {
  const o = normalizeAirportCode(originCode)
  if (dest.airportCode.toUpperCase() === o) return true
  if (dest.cityCode && dest.cityCode.toUpperCase() === o) return true
  return false
}

/**
 * Builds a lookup map from airport code to country code using the full
 * airports list from GeoState. This allows us to resolve the country for
 * any AirportLocation (which only has a country *name*, not a code).
 */
export function buildAirportCountryMap(airports: Array<App.Airport> | undefined): Record<string, string> {
  const map: Record<string, string> = {}
  if (!airports) return map
  for (const airport of airports) {
    if (airport.countryCode) {
      map[airport.code.toUpperCase()] = airport.countryCode.toUpperCase()
    }
  }
  return map
}

/**
 * Determines if a destination airport is domestic (same country as the origin).
 *
 * Uses the airportCountryMap to look up the destination's country code,
 * because AirportLocation.country is a country NAME (e.g. "Australia")
 * while origin identifiers are country CODES (e.g. "AU").
 *
 * @param destination - The destination airport location
 * @param originCountryCode - The origin country code (e.g. "AU")
 * @param airportCountryMap - Map of airport code -> country code from geo.airports
 */
export function isDomesticDestination(
  destination: App.AirportLocation,
  originCountryCode: string | undefined,
  airportCountryMap: Record<string, string>,
): boolean {
  if (!originCountryCode || !airportCountryMap) return false

  const destCountryCode = airportCountryMap[destination.airportCode.toUpperCase()]
  if (destCountryCode) {
    return destCountryCode === originCountryCode.toUpperCase()
  }

  // Fallback: if the destination airport isn't in our airports list,
  // compare the country name field directly (won't match codes, but
  // handles edge cases where both happen to be names or codes)
  if (destination.country) {
    return destination.country.toLowerCase() === originCountryCode.toLowerCase()
  }

  return false
}

/**
 * Splits a list of destination airports into international and domestic groups.
 *
 * @param destinations - Array of popular destination airports
 * @param originCountryCode - The origin country code (e.g. "AU")
 * @param airportCountryMap - Map of airport code -> country code from geo.airports
 */
export function categoriseDestinationsByCountry(
  destinations: Array<App.AirportLocation>,
  originCountryCode: string | undefined,
  airportCountryMap: Record<string, string>,
): CategorisedDestinations {
  const international: Array<App.AirportLocation> = []
  const domestic: Array<App.AirportLocation> = []

  for (const dest of destinations) {
    if (isDomesticDestination(dest, originCountryCode, airportCountryMap)) {
      domestic.push(dest)
    } else {
      international.push(dest)
    }
  }

  return { international, domestic }
}
